import { useMemo } from "react";
import { animationUrlFor } from "@jackedlogs/shared";
import type { EffortScale } from "@jackedlogs/shared";
import {
  useAthleteTargets,
  useMaxesData,
  usePrograms,
  uuidv4,
  type LoggerHostDeps,
} from "@jackedlogs/ui";

import { apiFetch, ApiError } from "../api";
import { durableWrite } from "../durableWrite";
import { confirmViaWindow, notifyViaWindow } from "../confirm";
import { displayUnitOf, useProfileDoc } from "../useProfileDoc";
import {
  invalidateSelfSessionResults,
  selfSessionResultsDeps,
} from "../progress/progressDeps";
import { fetchAndPersistExercises, loadCachedExercises } from "./exercisesCache";
import { ExercisePickerMount } from "../ExercisePickerMount";
import { ExerciseTimerSheet } from "./ExerciseTimerSheet";
import { EffortScalePicker } from "./EffortScalePicker";
import { PostSessionSummaryCard } from "./PostSessionSummaryCard";
import { PainDot, usePainRiskLabel } from "../PainDotWeb";

// Assembles web-rnw's LoggerHostDeps (RW-18) — the web twin of
// apps/mobile/src/screens/useLoggerHostDeps.ts. Every field mirrors the
// mobile hook's SHAPE (see hostDeps.ts for the full contract); the
// difference is always the platform mechanism behind it, never the data:
//   - durableWrite/shouldQueueError/queueWorkout: web has no offline queue
//     (seams.ts) — every write is online-only, `sent` or `rejected`, never
//     `queued`; queueWorkout always rejects (nothing to enqueue INTO).
//   - catalog/profile/programs/targets/maxes: web's own network-only
//     equivalents of the AsyncStorage-backed mobile modules, several
//     already shared via @jackedlogs/ui (useAthleteTargets, useMaxesData,
//     usePrograms) rather than duplicated per-host.
//   - ExercisePickerMount/ExerciseTimerSheet/EffortScalePicker/
//     PostSessionSummaryCard: web's own thin mounts of the shared
//     component trees (see each file's own header comment).
//   - timerNotification/sentryWorkoutContext: omitted (capability-gating
//     by omission — native-only slots, see hostDeps.ts's own doc on each).
//   - PainDot/usePainRiskLabel: wired 2026-08-17 via PainDotWeb.tsx —
//     painMap has been a web-rnw surface since the pain-map-picker lift
//     (2026-08-16), so this is no longer native-only.
export function useLoggerHostDeps(): LoggerHostDeps {
  const { doc, savePatch, refresh: refreshProfileDoc } = useProfileDoc();
  const { value: athleteTargets } = useAthleteTargets({ apiFetch });
  const programsState = usePrograms({ apiFetch, durableWrite, withProgress: false });
  const { maxMeta, loaded: maxesLoaded } = useMaxesData({ apiFetch });

  // LoggerProfile is `Omit<Profile, 'id'|'createdAt'|'updatedAt'>`;
  // useProfileDoc's doc is an untyped bag that starts `null` pre-load (web
  // has no ProfileSnapshot projection like mobile's always-defaulted
  // useProfile()). useLoggerScreen reads `profile.unitsPreference` and
  // `profile.effortScale` with NO `??` fallback of its own, so those two
  // need a real default here — same house defaults CatalogScreen/
  // WorkoutsScreen (kg) and EffortScreen (rir) already fall back to.
  // Everything else the Logger reads is optional-chained downstream.
  const profile = useMemo(
    () =>
      ({
        ...(doc ?? {}),
        name: (doc?.["name"] as string | undefined) ?? "",
        unitsPreference: displayUnitOf(doc),
        effortScale: (doc?.["effortScale"] as EffortScale | undefined) ?? "rir",
      }) as LoggerHostDeps["profile"],
    [doc],
  );

  return useMemo<LoggerHostDeps>(
    () => ({
      apiFetch,
      durableWrite,
      notify: notifyViaWindow,
      confirm: confirmViaWindow,
      profile,
      refreshProfile: refreshProfileDoc,
      patchProfile: async (patch) => {
        await savePatch(patch);
      },
      athleteTargets,
      sessionResultsDeps: selfSessionResultsDeps,
      followedPrograms: programsState.followedPrograms,
      maxMeta,
      maxesLoaded,
      catalog: {
        loadCached: loadCachedExercises,
        fetchAndPersist: fetchAndPersistExercises,
      },
      // Web has no offline queue (seams.ts) — every failure surfaces.
      shouldQueueError: () => false,
      queueWorkout: async () => {
        throw new ApiError(0, "no_offline_queue", null);
      },
      saveNoteEvent: async (note) => {
        try {
          await apiFetch("/api/me/workout-notes", {
            method: "POST",
            body: { ...note, id: note.id ?? uuidv4() },
          });
        } catch {
          // Best-effort: the workout is already saved. Never throws.
        }
      },
      saveExerciseGroup: async (name, exerciseSlugs) => {
        await apiFetch("/api/me/exercise-groups", {
          method: "POST",
          body: { id: uuidv4(), name, exerciseSlugs },
        });
      },
      invalidateSessionResults: invalidateSelfSessionResults,
      ExercisePickerMount,
      ExerciseTimerSheet,
      EffortScalePicker,
      PostSessionSummaryCard,
      // AN-2/AN-3 — il gemello della riga in apps/mobile. Stesso resolver
      // condiviso, base diversa perche' la porta il bundle invece dell'APK.
      // ⛔ Sul web non serve nessuna cache nostra: e' la cache HTTP del
      // browser, e la GIF che l'atleta ha gia' aperto non si riscarica.
      animationSource: (slug, widthPx, variant) =>
        animationUrlFor(import.meta.env.VITE_ANIMATIONS_URL, slug, widthPx, variant),
      PainDot,
      usePainRiskLabel,
    }),
    [
      profile,
      refreshProfileDoc,
      savePatch,
      athleteTargets,
      programsState.followedPrograms,
      maxMeta,
      maxesLoaded,
    ],
  );
}
